/**
 * DLUX restock-alert form.
 * Vanilla JS. Idempotent: re-binds whenever a new form is rendered into the
 * DOM (e.g. after a variant change re-renders the buy-buttons section).
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var MIN_RENDER_AGE_MS = 2000;
  var GENERIC_ERROR =
    'Sorry, that did not work. Please try again or contact us at contact@dlux-essentials.com.';
  var TURNSTILE_WIDGETS = new WeakMap();

  function bindAll(root) {
    var scope = root || document;
    var forms = scope.querySelectorAll('[data-dlux-restock-form]');
    for (var i = 0; i < forms.length; i++) {
      bind(forms[i]);
    }
  }

  function bind(container) {
    if (container.dataset.dluxRestockBound === '1') return;
    container.dataset.dluxRestockBound = '1';

    var formEl = container.querySelector('[data-dlux-restock-form-element]');
    var emailInput = container.querySelector('[data-dlux-restock-email]');
    var errorEl = container.querySelector('[data-dlux-restock-error]');
    var successEl = container.querySelector('[data-dlux-restock-success]');
    var submitBtn = container.querySelector('[data-dlux-restock-submit]');
    var submitLabel = container.querySelector('[data-dlux-restock-submit-label]');
    var turnstileEl = container.querySelector('[data-dlux-restock-turnstile]');
    if (!formEl || !emailInput || !submitBtn) return;

    // Prefill for logged-in customers.
    if (!emailInput.value && window.customer && window.customer.email) {
      emailInput.value = window.customer.email;
    }

    // Render the Turnstile widget explicitly so we can call execute() on submit.
    if (turnstileEl && window.turnstile && typeof window.turnstile.render === 'function') {
      try {
        var widgetId = window.turnstile.render(turnstileEl, {
          sitekey: turnstileEl.getAttribute('data-sitekey'),
          callback: function (token) {
            container.dataset.dluxRestockTurnstileToken = token || '';
          },
          'error-callback': function () {
            container.dataset.dluxRestockTurnstileToken = '';
          },
        });
        TURNSTILE_WIDGETS.set(container, widgetId);
      } catch (e) {
        console.warn('[dlux-restock] Turnstile render failed', e);
      }
    } else if (turnstileEl && !window.turnstile) {
      // Defer: widget will be picked up by Turnstile auto-render via data-callback.
    }

    formEl.addEventListener('submit', function (event) {
      event.preventDefault();
      submit(container, formEl, emailInput, errorEl, successEl, submitBtn, submitLabel);
    });

    // Journey: form became visible on this page (PDP is OOS and form bound).
    trackJourney('notify_view', {
      product_handle: container.dataset.productHandle || null,
      product_id: container.dataset.productId || null,
      variant_id: container.dataset.variantId || null,
    });
  }

  // Fire-and-forget hook into the storefront journey tracker. Silent no-op
  // if journey-tracker.js hasn't loaded yet (e.g. ad-blocked).
  function trackJourney(type, data) {
    try {
      if (window.dluxJourney && typeof window.dluxJourney.track === 'function') {
        window.dluxJourney.track(type, data || {});
      }
    } catch (e) {
      /* noop */
    }
  }

  function showError(errorEl, msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }

  function clearError(errorEl) {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  function setBusy(submitBtn, submitLabel, busy, originalLabel) {
    submitBtn.disabled = busy;
    if (submitLabel) {
      submitLabel.textContent = busy ? 'Sending...' : originalLabel;
    }
  }

  function getTurnstileToken(container) {
    var existing = container.dataset.dluxRestockTurnstileToken;
    if (existing) return Promise.resolve(existing);

    var widgetId = TURNSTILE_WIDGETS.get(container);
    if (!window.turnstile || widgetId === undefined) return Promise.resolve('');

    try {
      window.turnstile.execute(widgetId);
    } catch (e) {
      console.warn('[dlux-restock] Turnstile execute failed', e);
      return Promise.resolve('');
    }

    return new Promise(function (resolve) {
      var start = Date.now();
      var iv = setInterval(function () {
        var t = container.dataset.dluxRestockTurnstileToken;
        if (t) {
          clearInterval(iv);
          resolve(t);
        } else if (Date.now() - start > 8000) {
          clearInterval(iv);
          resolve('');
        }
      }, 100);
    });
  }

  function submit(container, formEl, emailInput, errorEl, successEl, submitBtn, submitLabel) {
    clearError(errorEl);

    // Honeypot.
    var honey = formEl.querySelector('input[name="website"]');
    if (honey && honey.value.trim() !== '') return;

    // Time-based bot guard.
    var renderedAt = parseInt(container.dataset.renderedAt || '0', 10) * 1000;
    var ageMs = Date.now() - renderedAt;
    if (renderedAt > 0 && ageMs < MIN_RENDER_AGE_MS) return;

    var email = (emailInput.value || '').trim();
    if (!EMAIL_RE.test(email)) {
      showError(errorEl, 'Please enter a valid email');
      return;
    }

    var variantInput = formEl.querySelector('[data-dlux-restock-variant-input]');
    var variantId = variantInput ? variantInput.value : container.dataset.variantId;
    if (!variantId) {
      showError(errorEl, GENERIC_ERROR);
      return;
    }

    var marketingOptIn = !!formEl.querySelector('input[name="marketing_opt_in"]:checked');
    var endpoint = container.dataset.endpoint;
    var originalLabel = submitLabel ? submitLabel.textContent : '';
    var hasTurnstile = !!container.querySelector('[data-dlux-restock-turnstile]');

    setBusy(submitBtn, submitLabel, true, originalLabel);

    var tokenPromise = hasTurnstile ? getTurnstileToken(container) : Promise.resolve('');

    tokenPromise
      .then(function (token) {
        if (hasTurnstile && !token) {
          console.warn('[dlux-restock] Turnstile token missing, submitting without it');
        }
        var body = {
          email: email,
          variant_id: String(variantId),
          turnstile_token: token || '',
          marketing_opt_in: marketingOptIn,
          website: '',
          submitted_after_ms: ageMs,
        };
        return fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('http_' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || data.ok !== true) throw new Error('bad_response');
        formEl.hidden = true;
        if (successEl) successEl.hidden = false;
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push({
            event: 'dlux_restock_subscribe',
            product_id: container.dataset.productId,
            variant_id: variantId,
            already_subscribed: !!data.alreadySubscribed,
          });
        }
        trackJourney('notify_submit', {
          product_handle: container.dataset.productHandle || null,
          product_id: container.dataset.productId || null,
          variant_id: String(variantId),
          marketing_opt_in: marketingOptIn,
          already_subscribed: !!data.alreadySubscribed,
        });
      })
      .catch(function (err) {
        console.warn('[dlux-restock] submit failed', err);
        showError(errorEl, GENERIC_ERROR);
        setBusy(submitBtn, submitLabel, false, originalLabel);
        trackJourney('notify_submit_failed', {
          product_handle: container.dataset.productHandle || null,
          product_id: container.dataset.productId || null,
          variant_id: String(variantId),
          reason: String(err && err.message ? err.message : 'unknown').slice(0, 64),
        });
        // Reset turnstile so the next attempt gets a fresh token.
        var widgetId = TURNSTILE_WIDGETS.get(container);
        if (window.turnstile && widgetId !== undefined) {
          try {
            window.turnstile.reset(widgetId);
          } catch (e) {
            /* noop */
          }
        }
        container.dataset.dluxRestockTurnstileToken = '';
      });
  }

  // Turnstile auto-render path: support the data-callback name in the snippet.
  window.dluxRestockTurnstileCallback = function (token) {
    var forms = document.querySelectorAll('[data-dlux-restock-form]');
    for (var i = 0; i < forms.length; i++) {
      forms[i].dataset.dluxRestockTurnstileToken = token || '';
    }
  };

  // Re-bind whenever new nodes show up (e.g. after variant change re-renders
  // the buy-buttons section via Section Rendering API).
  var mo = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches('[data-dlux-restock-form]')) bind(n);
        bindAll(n);
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindAll(document);
      mo.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    bindAll(document);
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
